/**
 * The alert cluster: what is wrong, in the corner of the eye, with somewhere to
 * go about it.
 *
 * This is the one piece of Paradox furniture the settlement had nothing like.
 * Everything the game knows about a town in trouble was already computed and
 * already true — `fedFraction` has been on the World since the diet landed,
 * `rubbleCount` is a counter kept precisely so nobody has to scan for it,
 * `arrears` counts the seconds the purse has been short — and every one of them
 * was reachable only by opening a screen and reading a number. A player who does
 * not already know to look does not look.
 *
 * The rules this follows, and they are the whole design:
 *
 *   • **An alert is a thing you can do something about.** "A siege is coming" is
 *     not an alert, it is the game; the clock says so in letters an inch high.
 *     "Nobody is working your quarry" is an alert.
 *   • **Every alert goes somewhere.** Clicking one flies the camera to the thing
 *     it is about, or opens the screen that fixes it. An alert that only says a
 *     sentence is a log line, and there is already a log.
 *   • **Six at once, ranked.** A wall of them is a wall, and a player who learns
 *     that the cluster is usually full learns to stop reading it. Dire before
 *     warning, and inside a tone, the order below.
 *   • **Nothing here is per-frame.** The readers walk the building store; they
 *     run on a half-second tick and the result is held. See `Alerts.read`.
 */

import { Phase, RESOURCE_LABELS, Team, type ResourceKey } from '../sim/types.ts';
import type { World } from '../sim/world.ts';
import { buildingType } from '../data/buildings.ts';
import { heroPointsLeft } from '../sim/systems/hero.ts';
import { TICK_RATE } from '../sim/constants.ts';
import { CurtainWatch, tileX, tileY } from '../sim/systems/curtain.ts';
import { foodKindsShort } from '../sim/systems/settlement.ts';

/** How loud. Decides the colour and the sort. */
export type AlertTone = 'dire' | 'warn';

export interface Alert {
  /** Stable across ticks, so the DOM diff can leave an unchanged chip alone. */
  id: string;
  tone: AlertTone;
  /** One of the marks in `MARKS`. */
  mark: keyof typeof MARKS;
  /** Two or three words. It is read at a glance or not at all. */
  title: string;
  /** A sentence saying what is actually true, with the number in it. */
  body: string;
  /** What to do about it. Optional — some alerts are their own instruction. */
  fix?: string;
  /** Fly the camera here. */
  at?: [number, number];
  /** Open a screen instead. */
  open?: 'ledger' | 'sheet';
}

/**
 * The marks, drawn rather than lettered, for the reason the resource icons are:
 * a row of Unicode dingbats in one colour is a row a player cannot learn.
 *
 * `currentColor`, so the tone drives the colour from CSS and there is one place
 * that decides what dire looks like.
 */
export const MARKS = {
  /** An empty bowl. Hunger. */
  bowl:
    '<path fill="currentColor" d="M1.5 7h13c0 3.7-2.9 6.6-6.5 6.6S1.5 10.7 1.5 7z"/>' +
    '<path fill="currentColor" d="M0.6 5.6h14.8v1.1H0.6z" opacity="0.7"/>',
  /** A cracked wall. A breach. */
  breach:
    '<path fill="currentColor" d="M1.6 4h4.2v3.2H1.6zM10.2 4h4.2v3.2h-4.2z"/>' +
    '<path fill="currentColor" d="M1.6 8.3h3v3.2h-3zM12 8.3h2.4v3.2H12z"/>' +
    '<path fill="currentColor" d="M7.4 3.2 6 8.2h1.7l-1.1 4.8 3.2-5.6H8.1l1.2-4.2z" opacity="0.85"/>',
  /** A hammer at rest. Nobody working. */
  idle:
    '<path fill="currentColor" d="M3.4 2.6h7.2v3.1H3.4z"/>' +
    '<path fill="currentColor" d="M6.4 5.7h1.9v8h-1.9z" opacity="0.8"/>' +
    '<path fill="currentColor" d="M10.6 3.1 14 4.2v1.9l-3.4 1.1z" opacity="0.6"/>',
  /**
   * A heap of broken slabs. Rubble.
   *
   * Slabs and not triangles: three triangles on a baseline is the international
   * sign for *mountains*, which is what the first cut of this drew, on a HUD
   * that also has a map in it.
   */
  rubble:
    '<path fill="currentColor" d="M1.2 13.4 2.6 9.2 6.2 8.4 7.2 13.4z"/>' +
    '<path fill="currentColor" d="M7 13.4 8.4 7.2 12.2 6.2 13.4 13.4z" opacity="0.78"/>' +
    '<path fill="currentColor" d="M4.2 7.8 6.4 5.4 8 7 5.6 8.6z" opacity="0.55"/>' +
    '<path fill="currentColor" d="M12.8 13.4 13.4 10.2 15 10.8 14.8 13.4z" opacity="0.5"/>',
  /** A crossed-out purse. The wages are late. */
  purse:
    '<path fill="currentColor" d="M4.4 5.6h7.2c1.4 1.6 2.2 3.4 2.2 5.2 0 1.7-1.2 2.8-3 2.8H5.2c-1.8 0-3-1.1-3-2.8 0-1.8.8-3.6 2.2-5.2z"/>' +
    '<path fill="currentColor" d="M5.6 2.2h4.8v2.6H5.6z" opacity="0.7"/>',
  /** A brimming vessel. The warehouses are full. */
  brim:
    '<path fill="currentColor" d="M2.6 6.4h10.8v7.2H2.6z"/>' +
    '<path fill="currentColor" d="M1.6 4.6h12.8v1.6H1.6z" opacity="0.8"/>' +
    '<path fill="currentColor" d="M7.2 0.8h1.6v2.8h-1.6zM4.2 2 5.6 3.6 4.4 4.6 3 3zM11.8 2 10.4 3.6l1.2 1L13 3z" opacity="0.55"/>',
  /** A star. A point to spend. */
  star:
    '<path fill="currentColor" d="M8 1 9.9 6h5.3l-4.3 3.2 1.7 5.1L8 11.1l-4.6 3.2 1.7-5.1L0.8 6h5.3z"/>',
  /** A house with a crack. Something of yours is being knocked down. */
  ruin:
    '<path fill="currentColor" d="M8 2.2 14.6 7h-2v6.8H3.4V7h-2z"/>' +
    '<path fill="#12100e" d="M8.8 6.4 7 9.6h1.6l-1.4 4.2H6V7.6z" opacity="0.9"/>',
  /**
   * A felling axe. There is nothing standing that cuts wood.
   *
   * Diagonal haft, flared blade. The first cut was an upright stick with a
   * half-disc centred on it, which at fifteen pixels is a lollipop: a blade has
   * to sit at the *end* of the haft and off to one side, or the eye reads the
   * stick as a stem.
   */
  axe:
    '<path fill="currentColor" d="M3.8 13.1 5.2 13.9 11.2 4.9 9.8 4.1z" opacity="0.72"/>' +
    '<path fill="currentColor" d="M8.4 1.9 14.6 4.7 13.2 8.6 7.6 6.2z"/>' +
    '<path fill="#12100e" d="M11.6 4 13.4 4.8 12.4 7.4z" opacity="0.5"/>',
} as const;

/**
 * Sort order inside a tone. Lower is more urgent.
 *
 * A list rather than a number on each alert, because the ranking is a single
 * editorial judgement about the whole set and belongs in one place where it can
 * be argued with.
 */
const RANK: string[] = [
  'starving',
  'breach',
  'hearth',
  'nowood',
  'arrears',
  'ruin',
  'unstaffed',
  'rubble',
  'brim',
  'skill',
];

/** Never show more than this. See the note at the top about walls of them. */
const MAX_SHOWN = 6;

/**
 * How recently a building must have been hit to count as under attack. Three
 * seconds: long enough that a chip does not strobe between arrows, short enough
 * that it clears when the thing hitting it dies.
 */
const HIT_WINDOW = TICK_RATE * 3;

export class Alerts {
  private held: Alert[] = [];
  private timer = 0;
  private world: WeakRef<World> | null = null;
  /**
   * The wall reader, kept rather than made per call.
   *
   * It caches on `map.wallVersion` and the flood fill behind it is over the
   * whole map, so a fresh one every half second would be the most expensive
   * thing on this screen by an order of magnitude. One instance, and it answers
   * from cache on every tick where nobody has laid a stone.
   */
  private watch = new CurtainWatch();

  /**
   * The list, recomputed at most twice a second.
   *
   * Every reader below walks the building store, and two of them walk it for a
   * predicate that is false all run — a settlement is not usually starving. At
   * sixty frames a second over a four-hundred-building town that is a hundred
   * thousand reads a second to print nothing.
   */
  read(w: World, dt: number): Alert[] {
    this.timer -= dt;
    if (this.timer > 0 && this.world?.deref() === w) return this.held;
    this.timer = 0.5;
    this.world = new WeakRef(w);

    const out: Alert[] = [];
    // Battle mode has no economy, no buildings of the player's own and no town
    // to be in trouble. Every reader below would be answering about nothing.
    if (!w.battle) {
      this.hunger(w, out);
      this.hearth(w, out);
      this.breach(w, out);
      this.underAttack(w, out);
      this.timberLock(w, out);
      this.wages(w, out);
      this.unstaffed(w, out);
      this.rubble(w, out);
      this.brimming(w, out);
    }
    this.skillPoints(w, out);

    out.sort((a, b) => {
      if (a.tone !== b.tone) return a.tone === 'dire' ? -1 : 1;
      return RANK.indexOf(a.id) - RANK.indexOf(b.id);
    });
    this.held = out.slice(0, MAX_SHOWN);
    return this.held;
  }

  /**
   * Going hungry, and — since the diet grew kinds — *hungry for what*.
   *
   * The alert has always been able to say how many went unfed and never which
   * shelf was bare, which was tolerable while there was one shelf. With four,
   * "the granary is empty" and "the weirs are empty" are different problems with
   * different buildings on the other end of them, so the alert names the empty
   * ones. `foodKindsShort` leaves out a kind nothing you own produces: a town
   * with no weir is not short of fish, it simply does not fish.
   */
  private hunger(w: World, out: Alert[]): void {
    if (w.pop <= 0 || w.fedFraction >= 0.99) return;
    const short = Math.round(w.pop * (1 - w.fedFraction));
    const empty = foodKindsShort(w).map((k) => RESOURCE_LABELS[k].toLowerCase());
    const named =
      empty.length === 0
        ? ''
        : empty.length === 1
          ? ` No ${empty[0]} left.`
          : ` No ${empty.slice(0, -1).join(', ')} or ${empty[empty.length - 1]} left.`;
    out.push({
      id: 'starving',
      tone: w.fedFraction < 0.8 ? 'dire' : 'warn',
      mark: 'bowl',
      title: 'Going hungry',
      body: `${short} of ${w.pop} went unfed.${named} Morale is falling and it will keep falling.`,
      fix: 'A farm, a weir on the bank, a lodge in the deadwood — or an oven, which feeds furthest of all.',
      open: 'ledger',
    });
  }

  /**
   * The circuit is not closed.
   *
   * `CurtainWatch` has been able to answer this since the curtain landed and
   * only the wall-building tool ever asked. It is the single most expensive
   * thing a player can get wrong between waves — a ring of stone with one gap in
   * it stops nothing at all, and the gap is very often somewhere they are not
   * looking. Silent until there is a wall to have a hole in: a settlement with
   * no curtain at all has not made a mistake, it has made a choice.
   */
  private breach(w: World, out: Alert[]): void {
    const r = this.watch.update(w.map);
    if (r.tiles < 40 || r.sealed) return;
    const holes = r.breaches.length + r.ends.length;
    if (holes === 0) return;
    const k = r.breaches[0] ?? r.ends[0];
    out.push({
      id: 'breach',
      tone: w.phase === Phase.Siege ? 'dire' : 'warn',
      mark: 'breach',
      title: holes === 1 ? 'A hole in the wall' : `${holes} holes in the wall`,
      body: `${r.tiles} tiles of curtain are standing and the line is still open. Anything walking in can walk straight through.`,
      fix: 'Close it, or hang a gate in it. A ring with a gap stops nobody.',
      at: [tileX(k), tileY(k)],
    });
  }

  private hearth(w: World, out: Alert[]): void {
    const bs = w.buildings;
    const h = w.hearth;
    if (h < 0 || bs.slots.alive[h] === 0) return;
    const frac = bs.hp[h] / Math.max(1, bs.maxHp[h]);
    if (frac > 0.6) return;
    out.push({
      id: 'hearth',
      tone: 'dire',
      mark: 'ruin',
      title: 'The Hearth is failing',
      body: `${Math.round(frac * 100)}% of it is standing. When it goes out, the run is over.`,
      at: [bs.tx[h], bs.ty[h]],
    });
  }

  /**
   * Something of the player's is being knocked down right now.
   *
   * Deliberately not "a building is damaged" — damage is a fact about the past
   * and there is a whole repair economy for it. This is about the present tense,
   * which is the only tense a player can still do something in, and it points
   * the camera at the one taking the worst of it.
   */
  private underAttack(w: World, out: Alert[]): void {
    if (w.phase !== Phase.Siege) return;
    const bs = w.buildings;
    let worst = -1;
    let worstFrac = 1;
    let count = 0;
    for (let i = 0; i < bs.slots.high; i++) {
      if (bs.slots.alive[i] === 0) continue;
      if (bs.team[i] !== Team.Player) continue;
      if (w.tick - bs.lastHitTick[i] > HIT_WINDOW) continue;
      count++;
      const frac = bs.hp[i] / Math.max(1, bs.maxHp[i]);
      if (frac < worstFrac) {
        worstFrac = frac;
        worst = i;
      }
    }
    if (worst < 0) return;
    const def = buildingType(bs.typeId[worst]);
    out.push({
      id: 'ruin',
      tone: 'dire',
      mark: 'ruin',
      title: 'Under the hammer',
      body:
        count === 1
          ? `The ${def.name} is being cut down — ${Math.round(worstFrac * 100)}% left.`
          : `${count} buildings are being cut down. The ${def.name} is worst, at ${Math.round(worstFrac * 100)}%.`,
      at: [bs.tx[worst], bs.ty[worst]],
    });
  }

  /**
   * The timber lock, named before it closes.
   *
   * `systems/deadfall.ts` is the hatch that opens when a settlement has no camp,
   * no timber and no way to buy any — and the note on it says half the complaint
   * was the silence. This is the other half: a chip that appears while there is
   * still a way out, rather than a log line after there is not.
   */
  private timberLock(w: World, out: Alert[]): void {
    if (w.res.timber >= 60) return;
    const bs = w.buildings;
    for (let i = 0; i < bs.slots.high; i++) {
      if (bs.slots.alive[i] === 0 || bs.team[i] !== Team.Player) continue;
      // A foundation counts. It was paid for and it is coming.
      if ((buildingType(bs.typeId[i]).produces.timber ?? 0) > 0) return;
    }
    out.push({
      id: 'nowood',
      tone: w.res.timber < 25 ? 'dire' : 'warn',
      mark: 'axe',
      title: 'No wood coming in',
      body: `${Math.floor(w.res.timber)} timber left and nothing standing that cuts more.`,
      fix: 'Raise a Woodcutter’s Camp while you can still afford one.',
    });
  }

  private wages(w: World, out: Alert[]): void {
    if (w.arrears <= 0) return;
    out.push({
      id: 'arrears',
      tone: w.arrears > 30 ? 'dire' : 'warn',
      mark: 'purse',
      title: 'The wages are late',
      body: `${Math.floor(w.arrears)}s in arrears. Soldiers tolerate a late purse, and then they walk home.`,
      fix: 'Raise the tax band, sell at the market, or muster fewer.',
      open: 'ledger',
    });
  }

  /**
   * Finished buildings with nobody in them.
   *
   * Villagers assign themselves, so this is never an order that was not given —
   * it is the settlement telling the player it has run out of people, or that a
   * yard is somewhere nobody will walk to. Both are worth knowing and neither is
   * visible anywhere else without opening every building in the town.
   */
  private unstaffed(w: World, out: Alert[]): void {
    const bs = w.buildings;
    let count = 0;
    let first = -1;
    for (let i = 0; i < bs.slots.high; i++) {
      if (bs.slots.alive[i] === 0 || bs.team[i] !== Team.Player || bs.build[i] < 1) continue;
      if (buildingType(bs.typeId[i]).workers <= 0) continue;
      if (bs.workers[i] > 0) continue;
      count++;
      if (first < 0) first = i;
    }
    if (count === 0) return;
    out.push({
      id: 'unstaffed',
      tone: 'warn',
      mark: 'idle',
      title: count === 1 ? 'A yard stands empty' : `${count} yards stand empty`,
      body:
        count === 1
          ? `The ${buildingType(bs.typeId[first]).name} has nobody working it.`
          : `${count} finished buildings have nobody working them.`,
      fix: 'More houses make more people. A yard nobody walks to gets nobody.',
      at: [bs.tx[first], bs.ty[first]],
    });
  }

  private rubble(w: World, out: Alert[]): void {
    if (w.rubbleCount <= 0) return;
    out.push({
      id: 'rubble',
      tone: 'warn',
      mark: 'rubble',
      title: 'Rubble to clear',
      body: `${w.rubbleCount} tiles are still under wreckage. Nothing can be raised on them until they are cleared.`,
      fix: 'Idle villagers clear it themselves. It is only a queue.',
    });
  }

  /**
   * A resource at the brim, with something still making it.
   *
   * The chip on the strip already goes gold when a stock is capped, which says
   * *that* it is full and not that the difference is being thrown away. This
   * says the second thing, which is the one that is costing the player
   * something.
   */
  private brimming(w: World, out: Alert[]): void {
    const cap = w.resCap;
    const full: ResourceKey[] = [];
    for (const k of ['timber', 'planks', 'stone', 'iron', 'grain', 'bread'] as ResourceKey[]) {
      if (w.res[k] >= cap - 1) full.push(k);
    }
    if (full.length === 0) return;
    out.push({
      id: 'brim',
      tone: 'warn',
      mark: 'brim',
      title: full.length === 1 ? 'A store is full' : `${full.length} stores are full`,
      body: `At the brim of ${Math.floor(cap)}: ${full.map((k) => RESOURCE_LABELS[k].toLowerCase()).join(', ')}. Everything made past that is lost.`,
      fix: 'A Storehouse widens every cap at once. Or spend it.',
      open: 'ledger',
    });
  }

  private skillPoints(w: World, out: Alert[]): void {
    if (w.hero.classId === '') return;
    const left = heroPointsLeft(w);
    if (left <= 0) return;
    out.push({
      id: 'skill',
      tone: 'warn',
      mark: 'star',
      title: left === 1 ? 'A point unspent' : `${left} points unspent`,
      body: 'He has levels he has not taken anything for.',
      open: 'sheet',
    });
  }
}
